// src/oura/components/ClientJump.tsx
//
// Jump to any client by typing its name (Jack, 22 Oct 2026). Staff only.
// Ctrl/Cmd K opens it from any Oura page; the client name at the top of a
// business's menu opens it too (openClientJump). Picking a client keeps the
// screen you are on: from Nyonya Table's Delivery you land on the other
// client's Delivery, not on its Overview.

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { Building2Icon, CornerDownLeftIcon, SearchIcon } from "lucide-react";
import { listBusinesses, type AeoBusiness } from "@service/aeo";
import { LogoSquare, SURFACE, TYPE, faviconOf } from "@oura/ui";
import { cn } from "../lib/cn";
import { OURA_ROOT } from "../nav";
import { useOuraRole } from "../role";

const OPEN_EVENT = "oura:client-jump";
const MAX_SHOWN = 8;

/** Opens the jump from anywhere (the business menu's client name). */
export function openClientJump(): void {
    window.dispatchEvent(new Event(OPEN_EVENT));
}

/** The same screen on another client: /oura/businesses/<id>/<rest>, or its Overview from anywhere else. */
export function jumpHref(pathname: string, id: string): string {
    const base = `${OURA_ROOT}/businesses/`;
    const enc = encodeURIComponent(id);
    if (!pathname.startsWith(base)) return `${base}${enc}`;
    const [current, ...rest] = pathname.slice(base.length).split("/");
    // The list's own pages (New scan) are not a client's screen.
    if (!current || current === "new") return `${base}${enc}`;
    // A draft or a single item belongs to the client it was opened on: keep only the screen.
    const screen = rest[0];
    return screen ? `${base}${enc}/${screen}` : `${base}${enc}`;
}

export function ClientJump() {
    const staff = useOuraRole() === "oura-staff";
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [pick, setPick] = useState(0);
    const [list, setList] = useState<AeoBusiness[] | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!staff) return;
        const show = () => {
            setQuery("");
            setPick(0);
            setOpen(true);
        };
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
                e.preventDefault();
                show();
            }
        };
        window.addEventListener("keydown", onKey);
        window.addEventListener(OPEN_EVENT, show);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener(OPEN_EVENT, show);
        };
    }, [staff]);

    // Read the list each time it opens: a client added a minute ago should be there.
    useEffect(() => {
        if (!open) return;
        let live = true;
        void listBusinesses().then((b) => live && setList(b));
        window.setTimeout(() => inputRef.current?.focus(), 0);
        return () => {
            live = false;
        };
    }, [open]);

    const matches = useMemo(() => {
        const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
        const all = (list ?? []).filter((b) => {
            const hay = `${b.business_name} ${b.domain}`.toLowerCase();
            return words.every((w) => hay.includes(w));
        });
        // A name that starts with what was typed first, then the rest by name.
        const first = words[0] ?? "";
        return all
            .sort((a, b) => Number(!a.business_name.toLowerCase().startsWith(first)) - Number(!b.business_name.toLowerCase().startsWith(first)) || a.business_name.localeCompare(b.business_name))
            .slice(0, MAX_SHOWN);
    }, [list, query]);

    if (!staff || !open) return null;

    const go = (b: AeoBusiness | undefined) => {
        if (!b) return;
        setOpen(false);
        navigate(jumpHref(pathname, b.id));
    };

    return createPortal(
        <div className="fixed inset-0 z-[1300] flex items-start justify-center bg-black/20 px-4 pt-[12vh]" onMouseDown={() => setOpen(false)}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Go to a client"
                className={cn(SURFACE.card, "w-full max-w-lg overflow-hidden")}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-2.5 border-b border-gray-100 px-4">
                    <SearchIcon aria-hidden className="h-4 w-4 shrink-0 text-gray-400" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setPick(0);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") setOpen(false);
                            else if (e.key === "ArrowDown") {
                                e.preventDefault();
                                setPick((p) => Math.min(p + 1, Math.max(0, matches.length - 1)));
                            } else if (e.key === "ArrowUp") {
                                e.preventDefault();
                                setPick((p) => Math.max(p - 1, 0));
                            } else if (e.key === "Enter") go(matches[pick]);
                        }}
                        placeholder="Go to a client"
                        aria-label="Go to a client"
                        className={cn(TYPE.tb, "h-12 w-full bg-transparent text-gray-900 placeholder:text-gray-400 focus:outline-none")}
                    />
                    <kbd className={cn(TYPE.tm, "shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-gray-500")}>Esc</kbd>
                </div>
                <div role="listbox" aria-label="Clients" className="max-h-[60vh] overflow-y-auto p-1.5">
                    {list === null ? (
                        <p className={cn(TYPE.tc, "m-0 px-3 py-4 text-gray-500")}>Reading your clients</p>
                    ) : matches.length === 0 ? (
                        <p className={cn(TYPE.tc, "m-0 px-3 py-4 text-gray-500")}>No client matches "{query.trim()}".</p>
                    ) : (
                        matches.map((b, i) => (
                            <button
                                key={b.id}
                                type="button"
                                role="option"
                                aria-selected={i === pick}
                                onMouseEnter={() => setPick(i)}
                                onClick={() => go(b)}
                                className={cn("flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left", i === pick ? "bg-gray-100" : "hover:bg-gray-50")}
                            >
                                <LogoSquare src={faviconOf(b.favicon_url, b.domain)} tone="gray" icon={Building2Icon} />
                                <span className="min-w-0 flex-1">
                                    <span className={cn(TYPE.tb, "block truncate font-medium text-gray-900")}>{b.business_name}</span>
                                    <span className={cn(TYPE.tc, "block truncate text-gray-500")}>{b.domain}</span>
                                </span>
                                {i === pick && <CornerDownLeftIcon aria-hidden className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
                            </button>
                        ))
                    )}
                </div>
                <p className={cn(TYPE.tm, "m-0 border-t border-gray-100 px-4 py-2 text-gray-500")}>
                    Opens the same screen on the client you pick. Ctrl K or ⌘K from any page.
                </p>
            </div>
        </div>,
        document.body,
    );
}
